import { useQuery, useQueryClient } from "@tanstack/react-query";
import { comUltimoConhecido, esquecerTudo, lembrado, lembrar } from "../offline/ultimoConhecido";
import { api, definirCsrf, ErroApi } from "./cliente";
import type { Sessao } from "./tipos";

export const CHAVE_SESSAO = ["sessao"] as const;

/** Sessão atual: `null` quando ninguém está logado. Sem internet, vale a última sessão conhecida. */
export function useSessao() {
  return useQuery({
    queryKey: CHAVE_SESSAO,
    queryFn: async (): Promise<Sessao | null> => {
      try {
        const sessao = await comUltimoConhecido("sessao", () => api<Sessao>("/auth/sessao"));
        definirCsrf(sessao.csrfToken);
        return sessao;
      } catch (erro) {
        if (erro instanceof ErroApi && erro.status === 401) {
          definirCsrf(null);
          esquecerTudo();
          return null;
        }
        throw erro;
      }
    },
    staleTime: Infinity,
    retry: false,
  });
}

/** Atualiza a sessão em cache após login, troca de senha ou logout. */
export function useDefinirSessao() {
  const qc = useQueryClient();
  return (sessao: Sessao | null) => {
    definirCsrf(sessao?.csrfToken);
    if (!sessao) {
      // Primeiro avisa as telas que a sessão acabou (elas levam ao login); só depois descarta os
      // dados do usuário. Limpar o cache inteiro antes deixava a tela presa "logada".
      qc.setQueryData(CHAVE_SESSAO, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== CHAVE_SESSAO[0] });
      esquecerTudo();
      return;
    }
    // Outro usuário entrou neste aparelho: nada do anterior pode sobrar.
    if (lembrado<Sessao>("sessao")?.usuario.id !== sessao.usuario.id) esquecerTudo();
    lembrar("sessao", sessao);
    qc.setQueryData(CHAVE_SESSAO, sessao);
  };
}
